/**
 * Compliance: dashboard (status counts + property × requirement matrix), list,
 * add record with certificate upload, detail with renewal and history.
 */
import { COMPLIANCE_STATUSES } from "@shared/constants.ts";
import { complianceItemSchema, complianceRenewSchema } from "@shared/schemas/operations.ts";
import { useState } from "react";
import { Card, Col, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { AttachOnSave, uploadAll } from "../../components/forms/AttachOnSave.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  CheckboxField,
  DateField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { FilterBar, FilterSelect } from "../../components/ui/Filters.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useUrlState } from "../../lib/url-state.ts";

export interface ComplianceRow {
  id: string;
  propertyId: string;
  typeId: string;
  typeName: string;
  issueDate: string | null;
  expiryDate: string | null;
  reference: string;
  contractorId: string | null;
  contractorName: string | null;
  notApplicable: boolean;
  status: string;
  daysToExpiry: number | null;
  notes: string;
  addressLine1: string;
  postcode: string;
  documentCount: number;
  supersededAt: string | null;
}

function expiryText(r: Pick<ComplianceRow, "daysToExpiry" | "status">) {
  if (r.daysToExpiry === null || r.status === "not_applicable" || r.status === "superseded") return "";
  if (r.daysToExpiry < 0) return `Expired ${-r.daysToExpiry} day${r.daysToExpiry === -1 ? "" : "s"} ago`;
  if (r.daysToExpiry === 0) return "Expires today";
  return `${r.daysToExpiry} day${r.daysToExpiry === 1 ? "" : "s"} left`;
}

export function ComplianceTable({ filters, compact }: { filters: Params; compact?: boolean }) {
  const f = useFormat();
  const q = useApi<ComplianceRow[]>("/api/compliance", filters);
  const columns: Column<ComplianceRow>[] = [
    { key: "type", header: "Requirement", cell: (r) => <span className="fw-semibold">{r.typeName}</span> },
    ...(compact ? [] : [{ key: "property", header: "Property", cell: (r: ComplianceRow) => r.addressLine1 }]),
    { key: "issued", header: "Issued", cell: (r) => f.date(r.issueDate) },
    {
      key: "expiry",
      header: "Expires",
      cell: (r) => (
        <>
          {f.date(r.expiryDate)}
          <div className="small text-muted">{expiryText(r)}</div>
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge status={r.status} label={f.label(COMPLIANCE_STATUSES, r.status)} />,
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          rowLink={(r) => `/compliance/${r.id}`}
          empty={
            compact ? (
              <p className="text-muted small p-3 mb-0">No compliance records.</p>
            ) : (
              <EmptyState
                icon="shield"
                title="No compliance records"
                action={
                  <IconButtonLink
                    to={withQuery("/compliance/new", { propertyId: filters.propertyId as string })}
                    icon="plus"
                  >
                    Add compliance record
                  </IconButtonLink>
                }
              >
                Track gas safety, EICR, EPC, insurance and licences. Reminders are generated before they
                expire.
              </EmptyState>
            )
          }
        />
      )}
    </QueryState>
  );
}

interface ComplianceDashboard {
  counts: Record<string, number>;
  properties: Array<{ id: string; reference: string; addressLine1: string; postcode: string }>;
  types: Array<{ id: string; name: string }>;
  matrix: Record<string, Record<string, { id: string; status: string; expiryDate: string | null }>>;
  attention: ComplianceRow[];
}

const CELL_CLASS: Record<string, string> = {
  current: "table-success",
  expiring: "table-warning",
  expired: "table-danger",
  not_applicable: "table-light",
};

export function ComplianceDashboardPage() {
  const f = useFormat();
  const { get } = useUrlState();
  const lookups = useLookups();
  const q = useApi<ComplianceDashboard>("/api/compliance/dashboard");
  return (
    <>
      <PageHeader
        title="Compliance"
        subtitle="Certificates, inspections, insurance and licences"
        actions={
          <Can permission="compliance:write">
            <IconButtonLink to="/compliance/new" icon="plus">
              Add compliance record
            </IconButtonLink>
          </Can>
        }
      />
      <QueryState query={q}>
        {(d) => (
          <>
            <Row className="g-3 mb-4">
              <Col xs={6} lg={3}>
                <StatCard
                  label="Expired"
                  value={d.counts.expired ?? 0}
                  tone="expense"
                  to="/compliance?status=expired#list"
                />
              </Col>
              <Col xs={6} lg={3}>
                <StatCard
                  label="Expiring soon"
                  value={d.counts.expiring ?? 0}
                  tone="warning"
                  to="/compliance?status=expiring#list"
                />
              </Col>
              <Col xs={6} lg={3}>
                <StatCard
                  label="Current"
                  value={d.counts.current ?? 0}
                  tone="income"
                  to="/compliance?status=current#list"
                />
              </Col>
              <Col xs={6} lg={3}>
                <StatCard label="Not applicable" value={d.counts.not_applicable ?? 0} />
              </Col>
            </Row>
            <Section title="Coverage by property" flush>
              {d.properties.length === 0 ? (
                <EmptyState icon="home" title="No properties yet">
                  Add a property to start tracking compliance.
                </EmptyState>
              ) : (
                <div className="table-responsive">
                  <Table bordered className="mb-0 matrix-table text-center align-middle">
                    <thead>
                      <tr>
                        <th className="text-start">Property</th>
                        {d.types.map((t) => (
                          <th key={t.id} style={{ maxWidth: 120, whiteSpace: "normal" }}>
                            {t.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.properties.map((p) => (
                        <tr key={p.id}>
                          <th className="text-start">
                            <Link to={`/properties/${p.id}?tab=compliance`}>{p.addressLine1}</Link>
                          </th>
                          {d.types.map((t) => {
                            const cell = d.matrix[p.id]?.[t.id];
                            return (
                              <td key={t.id} className={cell ? CELL_CLASS[cell.status] : ""}>
                                {cell ? (
                                  <Link to={`/compliance/${cell.id}`} className="text-reset">
                                    {cell.status === "not_applicable" ? "N/A" : f.date(cell.expiryDate)}
                                  </Link>
                                ) : (
                                  <Link
                                    to={`/compliance/new?propertyId=${p.id}&typeId=${t.id}`}
                                    className="text-muted small"
                                    aria-label={`Add ${t.name} for ${p.addressLine1}`}
                                  >
                                    +
                                  </Link>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Section>
          </>
        )}
      </QueryState>
      <Card id="list">
        <FilterBar>
          <FilterSelect
            param="status"
            label="Status"
            options={COMPLIANCE_STATUSES.filter((s) => s.value !== "superseded")}
          />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect param="typeId" label="Requirement" options={toOptions(lookups?.complianceTypes)} />
        </FilterBar>
        <ComplianceTable
          filters={{ status: get("status"), propertyId: get("propertyId"), typeId: get("typeId") }}
        />
      </Card>
    </>
  );
}

export function ComplianceFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  const [files, setFiles] = useState<File[]>([]);
  return (
    <EntityForm
      schema={complianceItemSchema}
      resource="/api/compliance"
      id={id}
      title={id ? "Edit compliance record" : "Add compliance record"}
      breadcrumbs={[{ label: "Compliance", to: "/compliance" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        typeId: params.get("typeId") ?? "",
        issueDate: "",
        expiryDate: "",
        reference: "",
        contractorId: "",
        notApplicable: false,
        notes: "",
      }}
      toForm={(r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]))}
      afterSave={async (saved) => uploadAll(files, "compliance", saved.id as string, "certificate")}
      redirect={(saved) => `/compliance/${saved.id}`}
      cancelTo={id ? `/compliance/${id}` : "/compliance"}
    >
      {(form) => {
        const type = lookups?.complianceTypes.find((t) => t.id === form.watch("typeId"));
        return (
          <Row>
            <Col md={6}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="Choose a property"
                required
              />
            </Col>
            <Col md={6}>
              <SelectField
                name="typeId"
                label="Requirement"
                options={toOptions(lookups?.complianceTypes, (t) => !t.isArchived)}
                placeholder="Choose a requirement"
                required
                hint={
                  type?.validityMonths
                    ? `Usually valid for ${type.validityMonths} months — expiry is calculated if left blank.`
                    : undefined
                }
              />
            </Col>
            <Col md={4}>
              <DateField name="issueDate" label="Issue / inspection date" />
            </Col>
            <Col md={4}>
              <DateField name="expiryDate" label="Expiry / review date" />
            </Col>
            <Col md={4}>
              <TextField name="reference" label="Certificate reference" />
            </Col>
            <Col md={6}>
              <SelectField
                name="contractorId"
                label="Carried out by"
                options={toOptions(lookups?.contractors)}
                placeholder="—"
              />
            </Col>
            <Col md={6} className="pt-md-4">
              <CheckboxField name="notApplicable" label="Not applicable to this property" />
            </Col>
            <Col md={12}>
              <TextAreaField name="notes" label="Notes" rows={2} />
            </Col>
            {!id && (
              <Col md={12}>
                <AttachOnSave files={files} onChange={setFiles} label="Upload certificate (optional)" />
              </Col>
            )}
          </Row>
        );
      }}
    </EntityForm>
  );
}

interface ComplianceDetail extends ComplianceRow {
  history: Array<{
    id: string;
    issueDate: string | null;
    expiryDate: string | null;
    reference: string;
    supersededAt: string | null;
  }>;
}

export function ComplianceDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  const lookups = useLookups();
  const q = useApi<ComplianceDetail>(`/api/compliance/${id}`);
  return (
    <QueryState query={q}>
      {(c) => (
        <>
          <PageHeader
            title={c.typeName}
            subtitle={
              <>
                <Link to={`/properties/${c.propertyId}`}>{c.addressLine1}</Link> ·{" "}
                <StatusBadge status={c.status} label={f.label(COMPLIANCE_STATUSES, c.status)} />{" "}
                <span className="small text-muted">{expiryText(c)}</span>
              </>
            }
            breadcrumbs={[{ label: "Compliance", to: "/compliance" }, { label: c.typeName }]}
            actions={
              <>
                {can("compliance:write") && !c.supersededAt && (
                  <ModalFormButton
                    label="Renew"
                    buttonSize="md"
                    variant="primary"
                    title={`Renew ${c.typeName}`}
                    schema={complianceRenewSchema}
                    defaults={{
                      issueDate: new Date().toISOString().slice(0, 10),
                      expiryDate: "",
                      reference: "",
                      contractorId: c.contractorId ?? "",
                      notes: "",
                    }}
                    onSubmit={async (v) => {
                      const created = await api.post<{ id: string }>(`/api/compliance/${c.id}/renew`, v);
                      navigate(`/compliance/${created.id}`);
                    }}
                  >
                    {() => (
                      <>
                        <DateField name="issueDate" label="New issue date" required />
                        <DateField
                          name="expiryDate"
                          label="New expiry date"
                          hint="Calculated from the requirement type if blank."
                        />
                        <TextField name="reference" label="Certificate reference" />
                        <SelectField
                          name="contractorId"
                          label="Carried out by"
                          options={toOptions(lookups?.contractors)}
                          placeholder="—"
                        />
                        <TextAreaField name="notes" label="Notes" rows={2} />
                        <p className="small text-muted mb-0">
                          Upload the new certificate on the next screen.
                        </p>
                      </>
                    )}
                  </ModalFormButton>
                )}
                <Can permission="compliance:write">
                  <IconButtonLink to={`/compliance/${c.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="compliance:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this compliance record?"
                    body="Use Renew for a new certificate instead, to keep the history."
                    onConfirm={async () => {
                      await api.delete(`/api/compliance/${c.id}`);
                      await invalidate();
                      navigate("/compliance");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Details">
                <DefinitionList
                  items={[
                    ["Issued", f.date(c.issueDate)],
                    ["Expires", f.date(c.expiryDate)],
                    ["Reference", c.reference],
                    [
                      "Carried out by",
                      c.contractorId ? (
                        <Link key="c" to={`/contractors/${c.contractorId}`}>
                          {c.contractorName}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    ["Notes", c.notes],
                  ]}
                />
              </Section>
              <Section title="History for this property" flush>
                <Table className="mb-0">
                  <thead>
                    <tr>
                      <th>Issued</th>
                      <th>Expires</th>
                      <th>Reference</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {c.history.map((h) => (
                      <tr key={h.id} className={h.id === c.id ? "table-primary" : undefined}>
                        <td>
                          <Link to={`/compliance/${h.id}`}>{f.date(h.issueDate)}</Link>
                        </td>
                        <td>{f.date(h.expiryDate)}</td>
                        <td>{h.reference || "—"}</td>
                        <td>
                          {h.supersededAt ? (
                            <StatusBadge status="superseded" />
                          ) : (
                            <StatusBadge status="current" label="latest" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
              <AuditHistory entityType="compliance" entityId={c.id} />
            </Col>
            <Col lg={4}>
              <Attachments entityType="compliance" entityId={c.id} title="Certificate" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
